import React, { useContext, Suspense, lazy as reactLazy } from 'react';
import { useSelector } from 'react-redux';
import _isEmpty from 'lodash/isEmpty';
import _isDate from 'lodash/isDate';
import PropTypes from 'prop-types';
import { normalizeNumber, roundUpCurrency } from '../../utilsV3/amountUtils';
import { parseDateOnly } from '../../utilsV3/dateUtils';
import { contributionsList } from './mockData';
import {
  GoalProgressMeter,
  CampaignImage,
  DonorWall,
  Title,
  AdvancedEndDateBadge,
} from '@paypalcorp/donate-react';
import MailingAddress from '../../shared/preview/MailingAddress';
import { BodyText, Button, LoadingSpinner } from '@paypalcorp/pp-react';
import withTheme from '../../componentsV3/withTheme';
import { isGuestFormPreview } from '../../utilsV3/campaignUtils';
import CampaignGuestFormPreview from './CampaignGuestFormPreview';
import classNames from 'classnames';
import PaymentCardPreview from './PaymentCardPreview';
import { MessageFormat } from '@paypalcorp/worldready';
import {
  Message,
  Context as WorldReadyContext,
} from '@paypalcorp/worldready-react';
import { formatCurrencyToParts } from '../../../utility/worldReadyUtils';

const DescriptionPreview = reactLazy(() =>
  import(/* webpackChunkName: "DescriptionPreview" */ './DescriptionPreview')
);

const GOAL_PROGRESS_METER_PREVIEW = {
  MAX_PROP: '413',
  VALUE_PROP: '94.54',
};

function CampaignCommonPreview({
  isMobile = false,
  hasScrolledToOffset,
  isMobilePreviewOpen = false,
  handleNext,
  isOverviewPage = false,
}) {
  const worldReady = useContext(WorldReadyContext);

  const donorName = new MessageFormat(worldReady, {
    id: 'setup.campaigns.campaignPreview.donorWall.donor',
  }).format({ donorNumber: '{donorNumber}' });

  const donorWallTitle = new MessageFormat(worldReady, {
    id: 'setup.campaigns.campaignPreview.donorWall.title',
  }).format();

  const donorWallAriaLabel = new MessageFormat(worldReady, {
    id: 'setup.campaigns.campaignPreview.donorWall.ariaLabel',
  }).format();

  const donorWallDate = new MessageFormat(worldReady, {
    id: 'setup.campaigns.campaignPreview.donorWall.date',
  }).format();

  const campaignTitle = new MessageFormat(worldReady, {
    id: 'setup.campaigns.campaignPreview.default.campaignTitle',
  }).format();

  const campaignImageHolderText = new MessageFormat(worldReady, {
    id: 'setup.campaigns.campaignPreview.campaignImage.holderText',
  }).format();

  const advancedEndDateBadgeContent = {
    minute: new MessageFormat(worldReady, {
      id: 'setup.campaigns.campaignPreview.campaigns.minute',
    }).format(),
    minutes: new MessageFormat(worldReady, {
      id: 'setup.campaigns.campaignPreview.campaigns.minutes',
    }).format(),
    hour: new MessageFormat(worldReady, {
      id: 'setup.campaigns.campaignPreview.campaigns.hour',
    }).format(),
    hours: new MessageFormat(worldReady, {
      id: 'setup.campaigns.campaignPreview.campaigns.hours',
    }).format(),
    day: new MessageFormat(worldReady, {
      id: 'setup.campaigns.campaignPreview.campaigns.day',
    }).format(),
    days: new MessageFormat(worldReady, {
      id: 'setup.campaigns.campaignPreview.campaigns.days',
    }).format(),
    month: new MessageFormat(worldReady, {
      id: 'setup.campaigns.campaignPreview.campaigns.month',
    }).format(),
    months: new MessageFormat(worldReady, {
      id: 'setup.campaigns.campaignPreview.campaigns.months',
    }).format(),
    year: new MessageFormat(worldReady, {
      id: 'setup.campaigns.campaignPreview.campaigns.year',
    }).format(),
    years: new MessageFormat(worldReady, {
      id: 'setup.campaigns.campaignPreview.campaigns.years',
    }).format(),
    endsInContent: new MessageFormat(worldReady, {
      id: 'setup.campaigns.campaignPreview.campaigns.endsInContent',
    }).format({ duration: '{duration}', unit: '{unit}' }),
    endedContent: new MessageFormat(worldReady, {
      id: 'setup.campaigns.campaignPreview.campaigns.endedContent',
    }).format({ date: '{date}' }),
  };

  const formValues = useSelector((state) => {
    return (
      state.form?.campaigns?.values ??
      state?.buttonManagerV3?.formattedCampaign ??
      {}
    );
  });
  const currentStep = useSelector((state) => state.campaigns.currentStep);

  const displayGuestFormPreview = isGuestFormPreview(currentStep);
  // return guest page preview on step 3
  if (displayGuestFormPreview) {
    return <CampaignGuestFormPreview />;
  }

  const receiverName = formValues?.receiverName;

  const eventTitle = formValues?.eventTitle;
  const eventDescription = formValues?.description;

  const {
    currencyCode,
    bannerImageUrl,
    noShipping,
    endTime,
    hasEndTime,
    targetAmount,
    status,
  } = formValues;
  const currencyParts = formatCurrencyToParts(0, { currency: currencyCode });
  const currencySymbol = currencyParts?.[0]?.value;
  let formattedEndTime;
  if (_isDate(endTime)) {
    formattedEndTime = new Date(endTime.getTime());
    formattedEndTime.setHours('23', '59', '59');
  } else {
    formattedEndTime = null;
  }

  const campaignGoal = !targetAmount ? '0' : normalizeNumber(targetAmount);

  const goalAmtWithCurrencySymbol = roundUpCurrency(campaignGoal, currencyCode);

  const campaignGoalTitle = new MessageFormat(worldReady, {
    id: 'setup.campaigns.campaignGoal.campaignGoal.title',
  }).format({ goalAmount: goalAmtWithCurrencySymbol });

  const showEndDatePreview = hasEndTime === 'yes' && endTime !== null;

  const donorWallList = {
    featured: [],
    list: contributionsList.map((txn, index) => ({
      ...txn,
      donor_name: donorName.replace('{donorNumber}', index + 1),
      formattedAmount: `${currencySymbol}Xx`,
      formattedDate: donorWallDate,
    })),
  };

  const renderHeader = () => (
    <div className="wrapper-header">
      {showEndDatePreview && (
        <div className="campaign-badge">
          <AdvancedEndDateBadge
            content={advancedEndDateBadgeContent}
            campaignStatus={status}
            endTime={formattedEndTime}
            currentTime={new Date()}
            formatDate={parseDateOnly}
          />
        </div>
      )}
      <div className="campaign-title">
        <Title
          size={hasScrolledToOffset || isMobile ? 'sm' : 'lg'}
          title={_isEmpty(eventTitle) ? campaignTitle : eventTitle}
        />
      </div>
      {isMobile && <BodyText>{receiverName}</BodyText>}
      <div className="campaign-progress">
        <GoalProgressMeter
          max={GOAL_PROGRESS_METER_PREVIEW.MAX_PROP}
          value={GOAL_PROGRESS_METER_PREVIEW.VALUE_PROP}
          steps={roundUpCurrency('0', currencyCode)}
          title={campaignGoalTitle}
        />
      </div>
    </div>
  );

  const classes = classNames({
    'preview-header': true,
    sticky: hasScrolledToOffset,
  });

  // show donate button when step 1 or 2 with mobile view and no mobile preview on step 1
  const showDonateButton =
    (currentStep === 1 || currentStep === 2) &&
    !isMobilePreviewOpen &&
    isMobile &&
    !isOverviewPage;

  const showNextButton =
    isMobile && isMobilePreviewOpen && (currentStep === 1 || currentStep === 2);

  return (
    <div className={isMobile ? 'campaign-common-preview-container' : ''}>
      <CampaignImage
        style={{
          height: isMobile ? '121.75px' : '380px',
          objectFit: 'contain',
        }}
        src={bannerImageUrl}
        content={{ holderText: campaignImageHolderText }}
      />
      <div className={classes}>{renderHeader()}</div>
      <div className="campaign-info-container">
        {renderHeader()}
        <div className="campaign-details">
          <div
            className={
              !isMobile ? 'rich-text-wrapper' : 'is-mobile-rte-wrapper'
            }
          >
            <Suspense
              fallback={
                <LoadingSpinner size="md" style={{ margin: '0 auto' }} />
              }
            >
              <DescriptionPreview
                id={isMobile ? 'mobileRTEPreview' : 'desktopRTEPreview'}
                eventDescription={eventDescription}
              />
            </Suspense>
          </div>

          {noShipping === 'no' && currentStep === 3 ? <MailingAddress /> : null}
          <DonorWall
            content={{
              title: donorWallTitle,
              ariaLabel: donorWallAriaLabel,
            }}
            contributions={donorWallList}
            totalTransactions={10}
            currentPage={1}
            transactionsPerPage={10}
            isLoading={false}
            isPreviewMode={true}
          />
          {noShipping === 'no' && currentStep === 3 ? <MailingAddress /> : null}
        </div>
        {!isMobile && <PaymentCardPreview />}
        {(showNextButton || isOverviewPage) && (
          <div
            className="common-preview-btn-next"
            style={{ pointerEvents: 'auto' }}
          >
            <Button tertiary onClick={handleNext}>
              <Message id="setup.campaigns.campaignPreview.preview.nextButton" />
            </Button>
          </div>
        )}
        {showDonateButton && (
          <div className="common-preview-btn">
            <Button>
              <Message id="setup.campaigns.campaignPreview.preview.donateButton" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

CampaignCommonPreview.propTypes = {
  campaignImageUrl: PropTypes.string,
  hasScrolledToOffset: PropTypes.bool,
  isMobile: PropTypes.bool,
  isMobilePreviewOpen: PropTypes.bool,
  handleNext: PropTypes.func,
  isOverviewPage: PropTypes.bool,
};

export default withTheme(CampaignCommonPreview);
